import React, { ReactNode } from 'react'

import Gap from '@/common/components/Gap'
import Link from '@/common/components/Link'
import useDevice from '@/common/hooks/useDevice'
import useI18n, { Translation } from '@/common/hooks/useI18n'
import { dateToString, numberToString } from '@/common/utils/string'
import { resolvePath } from '@/common/utils/url'
import {
  OWNER,
  OWNER_CONTACT,
  PLACE_RATING,
  ROOM_STATUS,
  DEAL_COUNTS,
  REQUEST_DATE,
  APPROVAL_DATE,
  DEALS,
  DEALS_DETAIL,
} from '@/modules/me/pages/BrokerListingsPage/translation'
import { ME_BROKER_LISTING_DEALS_PATH } from '@/modules/me/paths'
import { AVAILABLE, UNAVAILABLE } from '@/modules/property/translation'

import { Title, Value, DotStatus } from './styled'

interface BrokerListingsMoreDetailProps {
  brokerListing: RentAPerfect.BrokerListing
}

interface ItemProps {
  children: ReactNode
  title: Translation
}

const Item = ({ children, title }: ItemProps) => {
  const { t } = useI18n()

  return (
    <Gap className='align-start' space={10}>
      <Title>{t(title)}:</Title>
      <Value>{children}</Value>
    </Gap>
  )
}

const BrokerListingsMoreDetail = ({
  brokerListing,
}: BrokerListingsMoreDetailProps) => {
  const { t } = useI18n()
  const { isMobile } = useDevice()

  const { Room: room } = brokerListing

  return (
    <Gap space={12} vertical>
      {room?.Owner?.user ? (
        <Item title={OWNER}>
          {room.Owner.user.firstName} {room.Owner.user.lastName}
        </Item>
      ) : null}
      {room?.Owner ? (
        <Item title={OWNER_CONTACT}>{room.Owner.phoneNumber}</Item>
      ) : null}
      {room?.averageReview ? (
        <Item title={PLACE_RATING}>
          {numberToString(room.averageReview, { decimals: 1 })}
          {room.voterNumber ? (
            <span className='text-gray ml-4'>
              ({numberToString(room.voterNumber)})
            </span>
          ) : null}
        </Item>
      ) : null}
      <Item title={ROOM_STATUS}>
        <Gap className='align-center' space={4}>
          <DotStatus $isAvailable={room?.isAvailable ?? false} />
          <div>{t(room?.isAvailable ? AVAILABLE : UNAVAILABLE)}</div>
        </Gap>
      </Item>
      {brokerListing.isApproved ? (
        <Item title={DEAL_COUNTS}>
          {numberToString(room?._count?.UserContracts ?? 0)}
        </Item>
      ) : null}
      <Item title={REQUEST_DATE}>{dateToString(brokerListing.createdAt)}</Item>
      {brokerListing.isApproved ? (
        <Item title={APPROVAL_DATE}>
          {dateToString(brokerListing.updatedAt)}
        </Item>
      ) : null}
      {isMobile && brokerListing.isApproved ? (
        <Item title={DEALS}>
          <Link
            className='text-primary text-semibold'
            to={resolvePath(ME_BROKER_LISTING_DEALS_PATH, {
              roomId: room?.id as string,
            })}
          >
            {t(DEALS_DETAIL)}
          </Link>
        </Item>
      ) : null}
    </Gap>
  )
}

export default BrokerListingsMoreDetail
